import { Zone, Gateway, Logger } from '../../api'

const initialState = () => {
    return {
        zonesArray: [],
        defaultZoneID: 'Z00',
    }
}

export const zones = {
    namespaced: true,
    state: initialState(),
    getters: {
        get_zones: (state) => {
            if (state.zonesArray.length === 0) {
                return state.zonesArray = JSON.parse(localStorage.getItem('zones')) || []
            }
            return state.zonesArray.filter((zone) => zone !== null)
        },
    },
    actions: {
        async fetchZones({ state, commit }) {
            try {
                const zonesAPI = await Zone.get()
                if(!zonesAPI || zonesAPI.data.length === 0) {
                    commit('SET_ZONES', [])
                    return
                }
                const zonesAPIFormatted = zonesAPI.data.map((zone) => {
                    const { zoneAlias: name, zoneId: id, schedules } = zone
                    const zoneFormatted = { name, id, nodes: [], schedules }
                    return zoneFormatted
                })
                const dataSortedByZones = zonesAPIFormatted.sort(
                    (zoneA, zoneB) => (zoneA.name > zoneB.name ? 1 : -1)
                )
                const reorderedZonesArray = dataSortedByZones.filter(
                    (zone) => zone.id !== state.defaultZoneID
                )
                const defaultZone = dataSortedByZones.find(
                    (zone) => zone.id === state.defaultZoneID
                )
                if (defaultZone) {
                    reorderedZonesArray.unshift(defaultZone)
                }
                commit('SET_ZONES', reorderedZonesArray)
            } catch (error) {
                commit('SET_ZONES', [])
                throw { type: 'zone', error }
            }
        },
        async createZoneID({ state }) {
            let concatChars
            const letters = 'ABCDEFGHIJKLMNOPQRSTUVW'
            const numbers = '0123456789'
            const zones = state.zonesArray
            const zonesIDsInUse = zones.map((zone) => zone.id.substr(1, 2))
            const zonesIDsReserved = ['00', 'XY']
            const zonesIDsToAvoid = zonesIDsInUse.concat(zonesIDsReserved)

            do {
                const charsArray = []
                for (let index = 0; index < 2; index++) {
                    const isLetter = Math.random() < 0.5
                    let charSelected = isLetter
                        ? letters.charAt(
                              Math.floor(Math.random() * letters.length)
                          )
                        : numbers.charAt(
                              Math.floor(Math.random() * numbers.length)
                          )
                    charsArray.push(charSelected)
                }
                concatChars = charsArray.join('')
            } while (zonesIDsToAvoid.includes(concatChars))

            return concatChars
        },
        async addZone({ commit, dispatch }, zoneInfo) {
            let zoneStatus = true
            try {
                const { zoneName } = zoneInfo
                const zoneId = await dispatch('createZoneID')
                const zoneObj = { name: zoneName, id: `Z${zoneId}` }
                await Zone.post(zoneObj)
                commit('ADD_ZONE', zoneObj)
                return { zoneStatus }
            } catch (error) {
                zoneStatus = false
                const errorMsg = error.message
                return { zoneStatus, errorMsg }
            }
        },
        async editZone({ commit }, zoneInfo) {
            let zoneStatus = true
            try {
                const { zoneName, zoneId } = zoneInfo
                const zoneParams = { id: zoneId, name: zoneName }
                await Zone.update(zoneParams)
                commit('EDIT_ZONE', zoneInfo)
                return { zoneStatus }
            } catch (error) {
                zoneStatus = false
                const errorMsg = error.message
                return { zoneStatus, errorMsg }
            }
        },
        async fetchMongoValidation({ state }, mongoStatus) {
            let mongoValidation
            const wait = (ms) =>
                new Promise((resolve) => setTimeout(resolve, ms))
            for (let index = 1; index <= 16; index++) {
                await wait(1000)
                mongoValidation = await Logger.getStatus(mongoStatus.data)
                if (!mongoValidation.data) continue
                if (mongoValidation.data) {
                    if (mongoValidation.data.status === 'RECEIVED') break
                }
            }
            return mongoValidation
        },
        async controlZones({ state, commit, dispatch, rootState }, zoneInfo) {
            try {
                const stateZones = state.zonesArray
                const { zones, config, gateway } = zoneInfo
                const control = config.A === 1

                let configFormatted = control
                    ? {
                          auto: config.A,
                          min: config.LL,
                          max: config.LH,
                          d: config.D,
                          it: config.T,
                      }
                    : { auto: config.A, il: config.LV }
            
                const user_id = rootState.user.data._id
                const mongoStatus = await Gateway.sendInstructions(
                    zones,
                    'zones',
                    config,
                    gateway,
                    user_id
                )

                const mongoValidation = await dispatch(
                    'fetchMongoValidation',
                    mongoStatus
                )

                if (mongoValidation.data.status !== 'RECEIVED')
                    return mongoValidation.data.status

                const zonesModified = zones.map((zone) => {
                    const zoneToIterate = stateZones.find(
                        (zoneToFind) => zoneToFind.id === zone
                    )
                    const { id, name, nodes } = zoneToIterate
                    const zoneObj = { id, name, nodes, config: configFormatted }
                    return zoneObj
                })

                commit('CONTROL_MULTIPLE_ZONES', zonesModified)
                return mongoValidation.data.status
            } catch {
                return 'ERROR'
            }
        },
        async changeNodeZone({ rootState, state, dispatch, commit }, data) {
            try {
                const { nodes, newZone } = data
                const zonesArray = state.zonesArray
                const newZoneInfo = zonesArray.find(
                    (zone) => zone.name === newZone
                )
                
                const gateway = rootState.gateways.mainGateway
                const user_id = rootState.user.data._id
                const mongoStatus = await Gateway.zoneSet(
                    newZoneInfo.id,
                    nodes.map((node) => node.id),
                    gateway,
                    user_id
                )
                const mongoValidation = await dispatch(
                    'fetchMongoValidation',
                    mongoStatus
                )

                if (mongoValidation.data.status !== 'RECEIVED')
                    return mongoValidation.data.status

                commit('MOVE_NODES_TO_OTHER_ZONE', data)

                return mongoValidation.data.status
            } catch {
                return 'ERROR'
            }
        },
        async deleteZones({ state, commit }, zoneInfo) {
            let deletedItem = true
            try {
                const { zoneIdx } = zoneInfo
                const zoneID = state.zonesArray[zoneIdx].id
                await Zone.deletion(zoneID)
                commit('DELETE_ZONE', zoneID)
                return { deletedItem }
            } catch (error) {
                deletedItem = false
                return { error, deletedItem }
            }
        },
    },
    mutations: {
        SET_ZONES(state, zones) {
            localStorage.setItem('zones', JSON.stringify(zones))
            state.zonesArray = JSON.parse(localStorage.getItem('zones'))
        },
        ADD_ZONE(state, zoneToAdd) {
            const zonesArray = state.zonesArray
            const defaultZone = zonesArray.find(
                (zone) => zone.id === state.defaultZoneID
            )
            const filteredZonesArray = zonesArray.filter(
                (zone) => zone.id !== state.defaultZoneID
            )
            const { id, name } = zoneToAdd
            const zoneObj = { id, name, nodes: [] }
            filteredZonesArray.push(zoneObj)
            filteredZonesArray.sort((a, b) => (a.name > b.name ? 1 : -1))
            filteredZonesArray.unshift(defaultZone)
            localStorage.setItem('zones', JSON.stringify(filteredZonesArray))
            state.zonesArray = JSON.parse(localStorage.getItem('zones'))
        },
        EDIT_ZONE(state, zoneToEdit) {
            const { zoneName, zoneId } = zoneToEdit
            const zonesArray = state.zonesArray
            const defaultZone = zonesArray.find(
                (zone) => zone.id === state.defaultZoneID
            )
            const filteredZonesArray = zonesArray.filter(
                (zone) => zone.id !== state.defaultZoneID
            )
            const zoneToUpdateIndex = filteredZonesArray.findIndex(
                (zone) => zone.id === zoneId
            )
            filteredZonesArray[zoneToUpdateIndex].name = zoneName
            filteredZonesArray.sort((a, b) => (a.name > b.name ? 1 : -1))
            filteredZonesArray.unshift(defaultZone)
            localStorage.setItem('zones', JSON.stringify(filteredZonesArray))
            state.zonesArray = JSON.parse(localStorage.getItem('zones'))
        },
        CONTROL_MULTIPLE_ZONES(state, zones) {
            const zonesArray = JSON.parse(localStorage.getItem('zones'))
            zones.forEach((zoneToIterate) => {
                const zoneIndex = zonesArray.findIndex(
                    (zone) => zone.id === zoneToIterate.id
                )
                zonesArray[zoneIndex] = zoneToIterate
            })
            localStorage.setItem('zones', JSON.stringify(zonesArray))
            state.zonesArray = JSON.parse(localStorage.getItem('zones'))
        },
        MOVE_NODES_TO_OTHER_ZONE(state, data) {
            const { nodes, newZone, oldZone } = data
            const zonesArray = JSON.parse(localStorage.getItem('zones'))

            const oldZoneIndex = zonesArray.findIndex(
                (zone) => zone.name === oldZone
            )
            const newZoneIndex = zonesArray.findIndex(
                (zone) => zone.name === newZone
            )

            const unmovedNodes = zonesArray[oldZoneIndex].nodes
            const movedNodes = zonesArray[newZoneIndex].nodes
            const remainingNodes = []

            unmovedNodes.forEach((node) => {
                const { id } = node
                const nodeFound = nodes.find((node) => node.id === id)
                if (nodeFound)
                    movedNodes.push({
                        id,
                        zone_id: zonesArray[newZoneIndex].id,
                    })
                else
                    remainingNodes.push({
                        id,
                        zone_id: zonesArray[oldZoneIndex].id,
                    })
            })

            zonesArray[oldZoneIndex].nodes = remainingNodes
            zonesArray[newZoneIndex].nodes = movedNodes

            localStorage.setItem('zones', JSON.stringify(zonesArray))
            state.zonesArray = JSON.parse(localStorage.getItem('zones'))
        },
        DELETE_ZONE(state, zoneToDelete) {
            const zonesArray = JSON.parse(localStorage.getItem('zones')) || []
            const filteredZones = zonesArray.filter(
                (zone) => zone.id !== zoneToDelete
            )
            localStorage.setItem('zones', JSON.stringify(filteredZones))
            state.zonesArray = JSON.parse(localStorage.getItem('zones'))
        },
        RESET(state) {
            Object.assign(state, initialState())
        }
    },
}
